/**
 * Quick View Modal Functionality
 * Handles quick view modal for product cards on collection pages
 */

class QuickViewModal {
  constructor() {
    this.modal = document.getElementById('quick-view-modal');
    this.backdrop = document.getElementById('quick-view-backdrop');
    this.closeBtn = document.getElementById('quick-view-close');
    this.quickViewBtns = document.querySelectorAll('[data-quick-view]');
    this.currentProduct = null;
    
    this.init();
  }

  init() {
    if (!this.modal) return;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Quick view buttons
    this.quickViewBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productHandle = btn.dataset.productHandle;
        this.openModal(productHandle);
      });
    });

    // Close modal events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeModal());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeModal());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    // Prevent body scroll when modal is open
    this.modal.addEventListener('transitionend', () => {
      if (this.modal.classList.contains('is-open')) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }

  async openModal(productHandle) {
    try {
      // Show loading state
      this.showLoading();
      this.modal.classList.add('is-open');

      // Fetch product data
      const product = await this.fetchProduct(productHandle);
      this.currentProduct = product;

      // Populate modal with product data
      this.populateModal(product);

    } catch (error) {
      console.error('Error opening quick view:', error);
      this.closeModal();
    }
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    this.currentProduct = null;
  }

  showLoading() {
    const title = document.getElementById('quick-view-title');
    const price = document.getElementById('quick-view-price');
    const description = document.getElementById('quick-view-description');
    const mainImage = document.getElementById('quick-view-main-image');
    const thumbnails = document.getElementById('quick-view-thumbnails');
    const variants = document.getElementById('quick-view-variants');

    if (title) title.textContent = 'Loading...';
    if (price) price.textContent = '';
    if (description) description.textContent = '';
    if (mainImage) mainImage.src = '';
    if (thumbnails) thumbnails.innerHTML = '';
    if (variants) variants.innerHTML = '';
  }

  async fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      throw new Error('Product not found');
    }
    return await response.json();
  }

  populateModal(product) {
    // Title
    const title = document.getElementById('quick-view-title');
    if (title) {
      title.textContent = product.title;
    }

    // Price
    const price = document.getElementById('quick-view-price');
    if (price) {
      const variant = product.variants[0];
      if (variant) {
        const priceFormatted = this.formatPrice(variant.price);
        const comparePrice = variant.compare_at_price;
        if (comparePrice && comparePrice > variant.price) {
          const comparePriceFormatted = this.formatPrice(comparePrice);
          price.innerHTML = `<span class="price-sale">${priceFormatted}</span> <span class="price-compare">${comparePriceFormatted}</span>`;
        } else {
          price.textContent = priceFormatted;
        }
      }
    }

    // Description
    const description = document.getElementById('quick-view-description');
    if (description) {
      description.innerHTML = product.description || 'No description available.';
    }

    // Images
    this.populateImages(product);

    // Variants
    this.populateVariants(product);

    // SKU
    const sku = document.getElementById('quick-view-sku');
    if (sku && product.variants[0]) {
      sku.textContent = `SKU: ${product.variants[0].sku || 'N/A'}`;
    }

    // Availability
    const availability = document.getElementById('quick-view-availability');
    if (availability) {
      const variant = product.variants[0];
      if (variant && variant.available) {
        availability.textContent = 'In Stock';
        availability.className = 'quick-view-modal__availability in-stock';
      } else {
        availability.textContent = 'Out of Stock';
        availability.className = 'quick-view-modal__availability out-of-stock';
      }
    }

    // Set up add to cart functionality
    this.setupAddToCart(product);
  }

  populateImages(product) {
    const mainImage = document.getElementById('quick-view-main-image');
    const thumbnails = document.getElementById('quick-view-thumbnails');

    if (!product.images || product.images.length === 0) return;

    // Main image
    if (mainImage) {
      mainImage.src = product.images[0];
      mainImage.alt = product.title;
    }

    // Thumbnails
    if (thumbnails) {
      thumbnails.innerHTML = '';
      product.images.forEach((image, index) => {
        const thumbnail = document.createElement('div');
        thumbnail.className = 'quick-view-modal__thumbnail';
        if (index === 0) thumbnail.classList.add('active');
        
        const img = document.createElement('img');
        img.src = image;
        img.alt = product.title;
        
        thumbnail.appendChild(img);
        thumbnail.addEventListener('click', () => {
          // Update main image
          if (mainImage) {
            mainImage.src = image;
          }
          
          // Update active thumbnail
          thumbnails.querySelectorAll('.quick-view-modal__thumbnail').forEach(thumb => {
            thumb.classList.remove('active');
          });
          thumbnail.classList.add('active');
        });
        
        thumbnails.appendChild(thumbnail);
      });
    }
  }

  populateVariants(product) {
    const variantsContainer = document.getElementById('quick-view-variants');
    if (!variantsContainer || !product.variants || product.variants.length <= 1) {
      if (variantsContainer) variantsContainer.innerHTML = '';
      return;
    }

    // Group variants by option name
    const optionGroups = {};
    product.options.forEach(option => {
      optionGroups[option.name] = {
        name: option.name,
        values: [...new Set(product.variants.map(v => v[`option${option.position}`]).filter(Boolean))]
      };
    });

    variantsContainer.innerHTML = '';
    Object.values(optionGroups).forEach(option => {
      const variantGroup = document.createElement('div');
      variantGroup.className = 'quick-view-modal__variant';
      
      const label = document.createElement('label');
      label.className = 'quick-view-modal__variant-label';
      label.textContent = option.name;
      
      const options = document.createElement('div');
      options.className = 'quick-view-modal__variant-options';
      
      option.values.forEach(value => {
        const optionBtn = document.createElement('button');
        optionBtn.className = 'quick-view-modal__variant-option';
        optionBtn.textContent = value;
        optionBtn.dataset.optionName = option.name;
        optionBtn.dataset.optionValue = value;
        
        optionBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectVariantOption(option.name, value);
        });
        
        options.appendChild(optionBtn);
      });
      
      variantGroup.appendChild(label);
      variantGroup.appendChild(options);
      variantsContainer.appendChild(variantGroup);
    });

    // Select first option by default
    this.selectVariantOption(Object.keys(optionGroups)[0], optionGroups[Object.keys(optionGroups)[0]].values[0]);
  }

  selectVariantOption(optionName, optionValue) {
    // Update visual selection
    const allOptions = document.querySelectorAll(`[data-option-name="${optionName}"]`);
    allOptions.forEach(option => {
      option.classList.remove('selected');
      if (option.dataset.optionValue === optionValue) {
        option.classList.add('selected');
      }
    });

    // Find matching variant
    const selectedVariant = this.findVariantByOptions();
    if (selectedVariant) {
      this.updateVariantInfo(selectedVariant);
    }
  }

  findVariantByOptions() {
    if (!this.currentProduct) return null;

    const selectedOptions = {};
    document.querySelectorAll('.quick-view-modal__variant-option.selected').forEach(option => {
      selectedOptions[option.dataset.optionName] = option.dataset.optionValue;
    });

    return this.currentProduct.variants.find(variant => {
      return Object.keys(selectedOptions).every(optionName => {
        const optionPosition = this.currentProduct.options.findIndex(opt => opt.name === optionName) + 1;
        return variant[`option${optionPosition}`] === selectedOptions[optionName];
      });
    });
  }

  updateVariantInfo(variant) {
    // Update price
    const price = document.getElementById('quick-view-price');
    if (price) {
      const priceFormatted = this.formatPrice(variant.price);
      const comparePrice = variant.compare_at_price;
      if (comparePrice && comparePrice > variant.price) {
        const comparePriceFormatted = this.formatPrice(comparePrice);
        price.innerHTML = `<span class="price-sale">${priceFormatted}</span> <span class="price-compare">${comparePriceFormatted}</span>`;
      } else {
        price.textContent = priceFormatted;
      }
    }

    // Update availability
    const availability = document.getElementById('quick-view-availability');
    if (availability) {
      if (variant.available) {
        availability.textContent = 'In Stock';
        availability.className = 'quick-view-modal__availability in-stock';
      } else {
        availability.textContent = 'Out of Stock';
        availability.className = 'quick-view-modal__availability out-of-stock';
      }
    }

    // Update SKU
    const sku = document.getElementById('quick-view-sku');
    if (sku) {
      sku.textContent = `SKU: ${variant.sku || 'N/A'}`;
    }

    // Update add to cart button
    const addToCartBtn = document.getElementById('quick-view-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  setupAddToCart(product) {
    const addToCartBtn = document.getElementById('quick-view-add-to-cart');
    const quantityInput = document.getElementById('quick-view-quantity');

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  async addToCart() {
    if (!this.currentProduct) return;

    const selectedVariant = this.findVariantByOptions();
    if (!selectedVariant) {
      alert('Please select a variant');
      return;
    }

    const quantity = parseInt(document.getElementById('quick-view-quantity').value) || 1;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: selectedVariant.id,
          quantity: quantity
        })
      });

      if (response.ok) {
        // Show success message
        this.showAddToCartSuccess();
        
        // Close modal after a short delay
        setTimeout(() => {
          this.closeModal();
        }, 1500);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  }

  showAddToCartSuccess() {
    const addToCartBtn = document.getElementById('quick-view-add-to-cart');
    if (addToCartBtn) {
      const originalText = addToCartBtn.textContent;
      addToCartBtn.textContent = 'Added to Cart!';
      addToCartBtn.style.background = '#28a745';
      
      setTimeout(() => {
        addToCartBtn.textContent = originalText;
        addToCartBtn.style.background = '';
      }, 1500);
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new QuickViewModal();
});
